import { Box, Typography, useMediaQuery, Dialog } from "@mui/material";
import styles from "./BabyWeekImages.module.scss";
import BabyImagesByWeek from "./BabyImagesTimeline";
import BackgroundLayout from "../../components/custom-backgrounds/BackgroundLayout";
import ImageThemeSelector from "./ImageThemeSelector";
import { useRef, useState } from "react";
import HeaderComponent from "../header-component/HeaderComponentV2";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import { useTranslation } from "react-i18next";
import { ImageTheme } from "../../utility/interfaces/babyWeekImageDataInterface";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { IMAGE_THEME } from "../../utility/constants/common-constants";
import { updatePatientPreference } from "../header-component/services/PatientUserPreferenceDataSlice";
import { enqueueSnackbar } from "notistack";
import { usePostUserPrefernceMutation } from "../baby-size-image-widget/services/PostUserPreferenceApiSlice";

interface BabyWeekImagesProps {
  open: boolean;
  onClose: () => void;
}

const BabyWeekImages: React.FC<BabyWeekImagesProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const topRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [postUserPreference] = usePostUserPrefernceMutation();

  const patientUserName = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.userName
  );
  const patientProfileId = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.patientProfileId
  );
  const patientPreferences =
    useAppSelector((state) => state.patientPreferences.patientPreferences) ||
    [];
  const imageThemeData = useAppSelector((state) => state.babySizeImageThemes);

  const preferredThemeId = patientPreferences.find(
    (pref) => pref.categoryCode === IMAGE_THEME
  )?.preferenceIdentifier;

  // 1) Helper to pick the initial theme
  const getInitialTheme = (): ImageTheme | null => {
    if (!imageThemeData?.imageThemes?.length) return null;
    const preferred = imageThemeData.imageThemes.find(
      (theme) => theme.imageThemeId.toString() === preferredThemeId
    );
    if (preferred) return preferred;
    return imageThemeData.imageThemes.find((t) => t.isDefaultTheme) || null;
  };

  // 2) Seed selectedTheme once at mount
  const [selectedTheme, setSelectedTheme] = useState<ImageTheme | null>(() =>
    getInitialTheme()
  );

  const scrollToTop = () => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };

  // 3) Keep your selector interaction
  const handleSelect = (theme: ImageTheme) => {
    setSelectedTheme(theme);
  };

  // 4) Save and close
  const handleThemeUpdate = async (theme: ImageTheme) => {
    const body = {
      patientProfileId,
      categoryCode: IMAGE_THEME,
      preference: theme.imageThemeId.toString(),
      createdBy: patientUserName,
    };

    try {
      await postUserPreference(body).unwrap();
      dispatch(
        updatePatientPreference({
          categoryCode: IMAGE_THEME,
          preferenceIdentifier: theme.imageThemeId.toString(),
        })
      );
    } catch (error) {
      enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
    } finally {
      onClose();
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
    >
      <Box ref={topRef} />
      {!isMobile && <HeaderComponent />}

      <BackgroundLayout
        variant={isMobile ? "babyWeekImageMobile" : "babyWeekImageDesktop"}
        height={{ xs: "380px", sm: "350px", md: "250px" }}
        backgroundColor="#eaf0f0"
        showFooter={true}
        backgroundFooterColor="#eaf0f0"
      >
        <Box>
          <Box>
            <ImageThemeSelector
              imageThemes={imageThemeData?.imageThemes}
              onThemeUpdate={handleThemeUpdate}
              handleBack={onClose}
              handleSelect={handleSelect}
              selectedThemeId={selectedTheme?.imageThemeId!}
            />
          </Box>
          <Box className={styles.contentWrapper}>
            <Typography
              variant="bodySmall"
              className={styles.disclaimerText}
            >
              {t("babyWeekImages.disclaimer")}
            </Typography>
            <Typography
              variant={isMobile ? "h5" :"h3"}
              pb={isMobile ? "20px" :"24px"}
            >
              {t("babyWeekImages.babySizeNote")}
            </Typography>
            {selectedTheme && (
              <BabyImagesByWeek
                babyweekData={selectedTheme}
                currentWeek={imageThemeData.currentPregnancyWeek}
              />
            )}
          </Box>
          <Box className={styles.backToTopWrapper}>
            <CustomButton
              variant="outlined"
              text={t("babyWeekImages.backToTop")}
              onClick={scrollToTop}
            />
          </Box>
        </Box>
      </BackgroundLayout>
    </Dialog>
  );
};

export default BabyWeekImages;
